import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/**
 * `<line-total>` is a small controller (renders nothing itself) that powers the
 * bespoke Black Estate purchase panel:
 *
 * - injects the live line total (unit price × quantity) into the quantity row
 * - injects the unit price suffix into the add to cart button ("Add to cart - $36"),
 *   followed by the struck-through compare-at price when the variant is on sale
 *
 * The unit price, compare-at price and money format are provided server-side via
 * data attributes.
 * On variant selection the picker only morphs itself, so the new unit price is
 * taken from the ProductSelectEvent's resolved variant resource (same pattern
 * as product-price.js). The attribute observer covers contexts where the whole
 * section is morphed instead (e.g. featured product).
 *
 * @extends {Component}
 */
class LineTotalComponent extends Component {
  /** @type {MutationObserver | undefined} */
  #priceObserver;

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#update);

    // Variant selection: the picker dispatches a ProductSelectEvent that
    // bubbles to the section; its promise resolves with the new variant.
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    // Re-render when the unit price changes (contexts that morph the section).
    this.#priceObserver = new MutationObserver(this.#update);
    this.#priceObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-unit-price', 'data-compare-at-price'],
    });

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#update);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
    this.#priceObserver?.disconnect();
  }

  /**
   * Updates the unit price from the newly selected variant.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const price = detail?.resource?.price;
        if (typeof price === 'number') {
          this.dataset.unitPrice = String(price);
          // Shopify's variant JSON carries compare_at_price as a number or null
          const compareAtPrice = detail?.resource?.compare_at_price;
          this.dataset.compareAtPrice = typeof compareAtPrice === 'number' ? String(compareAtPrice) : '';
        }
        this.#update();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[line-total] Event promise rejected:', error);
      });
  };

  /**
   * The product grid this controller belongs to (falls back to document).
   * @returns {ParentNode}
   */
  get #scope() {
    return this.closest('[data-product-grid-content]') ?? document;
  }

  /** @returns {number} unit price in the currency's minor units (cents) */
  get #unitPrice() {
    return Number.parseInt(this.dataset.unitPrice ?? '', 10);
  }

  /**
   * The compare-at price, only when it is higher than the unit price (i.e. the
   * variant is on sale); otherwise NaN.
   * @returns {number} compare-at price in the currency's minor units (cents)
   */
  get #compareAtPrice() {
    const compareAtPrice = Number.parseInt(this.dataset.compareAtPrice ?? '', 10);
    return compareAtPrice > this.#unitPrice ? compareAtPrice : Number.NaN;
  }

  /** @returns {number} the currently selected quantity */
  get #quantity() {
    const input = /** @type {HTMLInputElement | null} */ (
      this.#scope.querySelector('.product-details input[name="quantity"]')
    );
    const value = Number.parseInt(input?.value ?? '1', 10);
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  /** Recomputes and writes the injected prices. Arrow field so it stays bound. */
  #update = () => {
    const unitPrice = this.#unitPrice;
    if (Number.isNaN(unitPrice)) return;

    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const quantity = this.#quantity;
    const total = formatMoney(unitPrice * quantity, format, currency);

    const lineTotal = this.#lineTotalElement();
    if (lineTotal) lineTotal.textContent = total;

    const atcPrice = this.#addToCartPriceElement();
    if (!atcPrice) return;

    const compareAtPrice = this.#compareAtPrice;
    if (Number.isNaN(compareAtPrice)) {
      atcPrice.textContent = total;
      return;
    }

    // On sale: "$180 <s>$199</s>", with visually-hidden labels so the button's
    // accessible name reads "Sale price $180 Regular price $199"
    const compareAtTotal = formatMoney(compareAtPrice * quantity, format, currency);
    const regular = document.createElement('s');
    regular.className = 'compare-at-price';
    regular.append(this.#hiddenLabel(this.dataset.labelRegular), compareAtTotal);
    atcPrice.replaceChildren(this.#hiddenLabel(this.dataset.labelSale), total, ' ', regular);
  };

  /**
   * Builds a visually-hidden label span (empty fragment when no label is set).
   * @param {string | undefined} text
   * @returns {Node}
   */
  #hiddenLabel(text) {
    if (!text) return document.createDocumentFragment();
    const label = document.createElement('span');
    label.className = 'visually-hidden';
    label.textContent = `${text} `;
    return label;
  }

  /**
   * Lazily finds (or creates) the line-total element inside the quantity row.
   * The section can morph and wipe injected nodes, so this recreates as needed.
   * @returns {HTMLElement | null}
   */
  #lineTotalElement() {
    const wrapper = this.#scope.querySelector('.product-details .quantity-selector-wrapper');
    if (!wrapper) return null;

    let element = wrapper.querySelector(':scope > .be-line-total');
    if (!element) {
      element = document.createElement('span');
      element.className = 'be-line-total';
      element.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(element);
    }
    return /** @type {HTMLElement} */ (element);
  }

  /**
   * Lazily finds (or creates) the price suffix inside the add to cart button.
   * @returns {HTMLElement | null}
   */
  #addToCartPriceElement() {
    const content = this.#scope.querySelector('.product-details .add-to-cart-button .add-to-cart-text__content');
    if (!content) return null;

    let element = content.querySelector(':scope > .be-atc-price');
    if (!element) {
      element = document.createElement('span');
      element.className = 'be-atc-price';
      content.appendChild(element);
    }
    return /** @type {HTMLElement} */ (element);
  }
}

if (!customElements.get('line-total')) {
  customElements.define('line-total', LineTotalComponent);
}
